/**
 * "Estamos rodando dentro do app nativo?" — FONTE ÚNICA.
 *
 * Quatro frentes precisam da mesma resposta e por razões diferentes: o login
 * (Google recusa OAuth em WebView), a UI de compra (Apple 3.1.1), o
 * compartilhamento (a folha do sistema vem do plugin, não do `navigator`) e o
 * scanner (permissão de câmera é nativa). Quando cada uma responde por conta
 * própria, uma delas erra — é a doença do BUG-81, em que a busca do onboarding
 * e a da `/search` divergiram por terem motores separados.
 *
 * ⚠️ NUNCA responda isto com `"Capacitor" in window`. É o BUG-64:
 * `@capacitor/core` **cria essa global no import**, em qualquer navegador. A
 * global responde "o módulo foi avaliado", não "estamos em app nativo" — e o
 * sintoma medido foi o convite de instalação da PWA sumir para sempre depois
 * da primeira visita a `/search`, que é onde o chunk que importava a Capacitor
 * vivia. `isNativePlatform()` é a API que responde a pergunta certa, e ela
 * devolve `false` na web.
 */

import { Capacitor } from "@capacitor/core";

/** Plataformas nativas que o projeto empacota. */
export type NativePlatform = "ios" | "android";

/**
 * `true` somente dentro de um build Capacitor (iOS/Android). Web, PWA
 * instalada e webview de rede social são todos `false` — PWA instalada NÃO é
 * app nativo, e confundir as duas é o que faria a UI de compra sumir para
 * quem instalou pelo navegador, onde vender é permitido.
 */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // Ambiente sem a global (SSR, jsdom sem setup) degrada para "web".
    return false;
  }
}

/** `"ios"` | `"android"` dentro do app nativo; `null` na web. */
export function nativePlatform(): NativePlatform | null {
  try {
    if (!Capacitor.isNativePlatform()) return null;
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : null;
  } catch {
    return null;
  }
}
